import type { PlanPoint, PlanRoom } from "@sts/geometry";
import {
  DATASHEET_LABEL,
  NOISE_RULESETS,
  citeDatasheet,
  datasheetFor,
  datasheetSource,
  outdoorUnitById,
  resolveRuleset,
  type HeatPumpSpec,
  type Jurisdiction,
  type NoiseAssessmentPoint,
  type NoiseRules,
} from "@sts/rules-data";
import type { CheckState } from "./design-check-types.js";
import { assessNoise } from "./noise.js";
import {
  buildFitScene,
  openingsOnRun,
  segmentHitsConvex,
  wallRunOf,
  type FitScene,
  type WallRun,
} from "./plan-fit.js";
import { axesOf, placedCorners, type PlanObjectOnPlan, type PlanWall } from "./plan-geometry.js";

/**
 * The outdoor unit where it is drawn: against which wall, whether the
 * manufacturer's clearances round it are there, how many reflecting surfaces
 * surround it, and what it sounds like at the neighbour's window (MCS 020 a)).
 *
 * WHAT THE SCAN CAN AND CANNOT SAY. RoomPlan draws a wall's ROOM face. From
 * outside, a wall's outside face is that line moved out by the wall's
 * thickness, which the scan does not know. So every distance from the unit to
 * a wall is measured to the scanned face and is LONGER than the real gap to
 * the outside face by the wall's thickness. Where that could flip a verdict
 * the check either uses the thickness the designer entered, or says it cannot
 * decide — never assumes a thickness. Boundaries, fences, sheds and planting
 * are not in the scan at all; the front of the unit is "clear of everything
 * scanned", which the message says in those words.
 *
 * CONSERVATIVE WHERE IT HAS TO GUESS. Unstated mounting is taken as standing
 * on the ground (the ground then counts as a reflecting surface — louder);
 * an unstated barrier is "none" with a full view (no attenuation — louder); a
 * distance measured on the plan is horizontal, so shorter than the
 * straight-line distance to an upstairs window (louder). Each says so.
 */

type HeatPump = Extract<PlanObjectOnPlan, { kind: "heat-pump" }>;
type Point = PlanPoint;

/** Parallel within 5°, the plan's own tolerance for "along a wall". */
const PARALLEL_SIN = Math.sin((5 * Math.PI) / 180);
/**
 * How far behind the unit a wall may be and still be the one it stands at —
 * the same reach the plan uses to turn a dropped unit away from a wall.
 */
const HOST_WALL_REACH_M = 2;
/**
 * Walls whose scanned face is within 1 m + this of the unit MAY be within 1 m
 * once the wall's thickness is allowed for, and are named as "possible"
 * reflecting surfaces when the thickness is not entered. About the thickest
 * domestic wall — the plan's own figure for how far an opening may sit off its
 * wall line. A prompt to check, not a number in any result.
 */
const THICK_WALL_M = 0.4;
/** Two scanned walls within this of one line are one reflecting surface. */
const SAME_PLANE_M = 0.1;
const TOL_M = 0.0005;
/**
 * Clearance zones are widened by this across their narrow side, so a
 * neighbour lined up exactly with the unit — its edge ON the zone's edge — is
 * still seen. A millimetre: far below anything a scan resolves.
 */
const ZONE_GROW_M = 0.001;

const sub = (a: Point, b: Point): Point => ({ x: a.x - b.x, y: a.y - b.y });
const dot = (a: Point, b: Point): number => a.x * b.x + a.y * b.y;
const cross = (a: Point, b: Point): number => a.x * b.y - a.y * b.x;
const mmText = (m: number): string => `${Math.round(m * 1000).toLocaleString("en-GB")} mm`;

function segmentDistance(a: Point, b: Point, c: Point, d: Point): number {
  const pointSeg = (p: Point, s0: Point, s1: Point): number => {
    const e = sub(s1, s0);
    const len2 = dot(e, e);
    const t = len2 > 0 ? Math.min(Math.max(dot(sub(p, s0), e) / len2, 0), 1) : 0;
    return Math.hypot(p.x - (s0.x + e.x * t), p.y - (s0.y + e.y * t));
  };
  const d1 = cross(sub(b, a), sub(c, a));
  const d2 = cross(sub(b, a), sub(d, a));
  const d3 = cross(sub(d, c), sub(a, c));
  const d4 = cross(sub(d, c), sub(b, c));
  if (d1 * d2 < 0 && d3 * d4 < 0) return 0;
  return Math.min(pointSeg(a, c, d), pointSeg(b, c, d), pointSeg(c, a, b), pointSeg(d, a, b));
}

function polygonSegmentDistance(poly: readonly Point[], a: Point, b: Point): number {
  if (segmentHitsConvex(a, b, poly)) return 0;
  let best = Infinity;
  for (let i = 0; i < poly.length; i += 1) {
    best = Math.min(best, segmentDistance(poly[i]!, poly[(i + 1) % poly.length]!, a, b));
  }
  return best;
}

/** A rectangle in the unit's own frame (s along its width, r toward its front), as plan points. */
function localRect(unit: HeatPump, s0: number, s1: number, r0: number, r1: number): Point[] {
  const { u, v } = axesOf(unit.geometry.rotationDeg);
  const at = (s: number, r: number): Point => ({
    x: unit.geometry.x + u.x * s + v.x * r,
    y: unit.geometry.y + u.y * s + v.y * r,
  });
  return [at(s0, r0), at(s1, r0), at(s1, r1), at(s0, r1)];
}

// ------------------------------------------------------------------ types --

export type ClearanceSide = "back" | "front" | "left" | "right";

export interface ClearanceItem {
  readonly side: ClearanceSide | "sides";
  readonly state: CheckState;
  readonly requiredMm?: number;
  readonly message: string;
}

export interface ReflectingSurface {
  readonly kind: "ground" | "wall";
  /** Wall ids making up this surface (one plane). */
  readonly wallIds: readonly string[];
  /** Scanned-face distance from the unit, m. */
  readonly distanceM: number;
  readonly note?: string;
}

/** One outdoor unit's sound at an assessment position (MCS 020 a) Table 2, Steps 3-7a). */
export interface NoiseContribution {
  readonly objectId: string;
  /** "HP2" — the tag the plan exports and the installer pack print. */
  readonly tag: string;
  /** "HP2 (Vaillant aroTHERM plus 7 kW)" */
  readonly label: string;
  readonly soundPowerDbA: number;
  readonly q: number;
  /** To 0.1 m (Step 5). */
  readonly distanceM: number;
  readonly distanceBasis: "plan" | "typed";
  readonly barrierType: NoiseAssessmentPoint["barrierType"];
  readonly lineOfSight: NoiseAssessmentPoint["lineOfSight"];
  readonly barrierAttenuationDb: number;
  /**
   * True when this unit has no entry of its own for this window, so no barrier
   * and a full view are taken — the louder case — until one is added.
   */
  readonly barrierAssumed: boolean;
  /** Step 7a, dB(A) to 0.01 — never rounded to 0.1 before the Step 7b sum. */
  readonly levelDb: number;
}

/** An outdoor unit on the plan whose sound is NOT in a position's level, and why. */
export interface NoiseUnitLeftOut {
  readonly objectId: string;
  /** "HP2" */
  readonly tag: string;
  readonly label: string;
  /**
   *   no-sound-power  — it has no sound power entered, so it cannot be added yet;
   *   typed-distance  — the position is a typed distance, which is this unit's alone;
   *   other-floor     — it is on another floor, and floors are not scanned in one frame;
   *   at-the-window   — its centre is within 0.05 m of the point, so no distance to 0.1 m.
   */
  readonly reason: "no-sound-power" | "typed-distance" | "other-floor" | "at-the-window";
}

export interface NoisePositionResult {
  readonly id: string;
  readonly description: string;
  readonly state: CheckState;
  /** This unit's own distance to the position. */
  readonly distanceM?: number;
  readonly distanceBasis?: "plan" | "typed";
  /** This unit's own barrier attenuation. */
  readonly barrierAttenuationDb?: number;
  /**
   * The level at the position that is compared with the limit, rounded as
   * MCS 020 a) Step 8 requires: every outdoor unit that reaches it added
   * together (Step 7b) — which, for a unit on its own, is its own level.
   */
  readonly levelDb?: number;
  readonly limitDb: number;
  readonly message: string;
  readonly working: readonly string[];
  /**
   * Each outdoor unit added into `levelDb`, this unit first. Absent when this
   * unit alone reaches the position.
   */
  readonly contributions?: readonly NoiseContribution[];
  /** This unit's own level, rounded as Step 8 — present with `contributions`. */
  readonly unitLevelDb?: number;
  /** Outdoor units on the plan NOT added into `levelDb`, and why. Absent when none is left out. */
  readonly leftOut?: readonly NoiseUnitLeftOut[];
}

export interface OutdoorUnitCheck {
  readonly objectId: string;
  readonly name: string;
  readonly storey: number;
  /** "7 kW — the size on the box, not a sizing figure", or undefined for a custom unit. */
  readonly nominal?: string;
  readonly wall: {
    readonly state: CheckState;
    readonly message: string;
    readonly hostWallIds: readonly string[];
    /** Scanned-face distance from the unit's back to the wall, m. */
    readonly backGapM?: number;
  };
  readonly clearances: {
    readonly state: CheckState;
    /** Where the figures came from, in words. */
    readonly source: string;
    readonly items: readonly ClearanceItem[];
    readonly openings: readonly { readonly id: string; readonly label: string; readonly message: string }[];
    readonly aboveNote?: string;
  };
  readonly directivity: {
    readonly state: CheckState;
    /** The Q used: the designer's override, else the plan's. */
    readonly q: number;
    readonly derivedQ: number;
    readonly overridden: boolean;
    readonly surfaces: readonly ReflectingSurface[];
    readonly message: string;
    readonly notes: readonly string[];
  };
  readonly noise: {
    readonly state: CheckState;
    readonly message: string;
    readonly limitDb: number;
    readonly metric: string;
    readonly rulesetLabel: string;
    readonly soundPower?: { readonly dbA: number; readonly source: string };
    /** A datasheet sound power the designer may accept, with its label. */
    readonly soundPowerPrefill?: { readonly dbA: number; readonly condition: string; readonly citation: string; readonly label: string };
    readonly positions: readonly NoisePositionResult[];
    readonly notes: readonly string[];
  };
}

// ------------------------------------------------------------- the wall --

interface HostWall {
  readonly wall: PlanWall;
  readonly run: WallRun;
  /** Scanned-face distance from the unit face nearest the wall, m. */
  readonly gapM: number;
  readonly facesWall: boolean;
}

function hostWallOf(unit: HeatPump, walls: readonly PlanWall[]): HostWall | undefined {
  const { u, v } = axesOf(unit.geometry.rotationDeg);
  const c = { x: unit.geometry.x, y: unit.geometry.y };
  const W = unit.spec.widthMm / 1000;
  const D = unit.spec.depthMm / 1000;
  let best: { wall: PlanWall; gap: number; facesWall: boolean } | undefined;
  for (const wall of walls) {
    const d = sub(wall.end, wall.start);
    const length = Math.hypot(d.x, d.y);
    if (!(length > 1e-6)) continue;
    const dir = { x: d.x / length, y: d.y / length };
    if (Math.abs(cross(dir, u)) > PARALLEL_SIN) continue;
    // Outside this wall: the unit's centre on the side away from its room.
    if (dot(sub(c, wall.start), wall.inward) >= 0) continue;
    const outward = { x: -wall.inward.x, y: -wall.inward.y };
    const facesWall = dot(v, outward) < 0;
    const face = facesWall ? { x: c.x + (v.x * D) / 2, y: c.y + (v.y * D) / 2 } : { x: c.x - (v.x * D) / 2, y: c.y - (v.y * D) / 2 };
    const gap = dot(sub(face, wall.start), outward);
    if (gap < -0.01 || gap > HOST_WALL_REACH_M) continue;
    const t = dot(sub(c, wall.start), dir);
    if (t + W / 2 <= 0 || t - W / 2 >= length) continue;
    if (!best || gap < best.gap) best = { wall, gap, facesWall };
  }
  if (!best) return undefined;
  return { wall: best.wall, run: wallRunOf(best.wall, walls), gapM: Math.max(best.gap, 0), facesWall: best.facesWall };
}

// ------------------------------------------------------------ clearances --

interface ResolvedClearances {
  readonly back?: number;
  readonly front?: number;
  readonly left?: number;
  readonly right?: number;
  readonly above?: number;
  readonly source: string;
  readonly notes?: string;
  readonly any: boolean;
}

function resolveClearances(spec: HeatPumpSpec): ResolvedClearances {
  const sheet = datasheetFor(spec.catalogueId)?.clearances ?? null;
  const mine = spec.design?.clearancesMm ?? {};
  const pick = (side: ClearanceSide): number | undefined => {
    const own = mine[side];
    if (own !== undefined) return own;
    const theirs = sheet?.[side];
    return typeof theirs === "number" ? theirs : undefined;
  };
  const sides = { back: pick("back"), front: pick("front"), left: pick("left"), right: pick("right") };
  const designer = Object.values(mine).some((x) => x !== undefined);
  const fromSheet = sheet !== null;
  const source = designer
    ? `your figures${spec.design?.clearancesSource ? ` (${spec.design.clearancesSource})` : ""}${fromSheet ? ", the rest from the " + DATASHEET_LABEL : ""}`
    : fromSheet
      ? `${DATASHEET_LABEL}: ${citeDatasheet(datasheetSource(sheet.source))}${sheet.basis !== "minimum" ? ` (figures are ${sheet.basis})` : ""}`
      : "none";
  return {
    ...sides,
    ...(typeof sheet?.above === "number" ? { above: sheet.above } : {}),
    source,
    ...(sheet?.notes ? { notes: sheet.notes } : {}),
    any: Object.values(sides).some((x) => x !== undefined),
  };
}

interface Blocker {
  readonly label: string;
  readonly distanceM: number;
}

/** The nearest scanned wall (not one of `exclude`) or other outdoor unit inside `zone`. */
function blockerIn(
  zone: readonly Point[],
  edge: readonly [Point, Point],
  scene: FitScene,
  exclude: ReadonlySet<string>,
  others: readonly HeatPump[],
  thicknessM: number,
): Blocker | undefined {
  let best: Blocker | undefined;
  for (const segment of scene.wallSegments.values()) {
    if (exclude.has(segment.id)) continue;
    if (!segmentHitsConvex(segment.start, segment.end, zone)) continue;
    const distanceM = Math.max(segmentDistance(edge[0], edge[1], segment.start, segment.end) - thicknessM, 0);
    if (!best || distanceM < best.distanceM) {
      best = { label: `the wall ${segment.label} of ${segment.roomLabel}`, distanceM };
    }
  }
  for (const other of others) {
    const poly = placedCorners(other.geometry, other.spec.widthMm / 1000, other.spec.depthMm / 1000);
    let hit = false;
    for (let i = 0; i < poly.length; i += 1) {
      if (segmentHitsConvex(poly[i]!, poly[(i + 1) % poly.length]!, zone)) hit = true;
    }
    if (!hit) continue;
    let distanceM = Infinity;
    for (let i = 0; i < poly.length; i += 1) {
      distanceM = Math.min(distanceM, segmentDistance(edge[0], edge[1], poly[i]!, poly[(i + 1) % poly.length]!));
    }
    if (!best || distanceM < best.distanceM) best = { label: `the outdoor unit ${other.spec.name}`, distanceM };
  }
  return best;
}

function checkClearances(
  unit: HeatPump,
  host: HostWall | undefined,
  scene: FitScene,
  others: readonly HeatPump[],
): OutdoorUnitCheck["clearances"] {
  const c = resolveClearances(unit.spec);
  const W = unit.spec.widthMm / 1000;
  const D = unit.spec.depthMm / 1000;
  const thicknessMm = unit.spec.design?.wallThicknessMm;
  const t = thicknessMm !== undefined ? thicknessMm / 1000 : 0;
  const items: ClearanceItem[] = [];
  const hostIds = new Set(host ? [...host.run.wallIds] : []);
  const aboveNote =
    c.above !== undefined ? `Above the unit: ${c.above.toLocaleString("en-GB")} mm clear — not something a plan can show; check on site.` : undefined;

  // Openings on the wall behind the unit, within its width and its side clearances.
  const openings: { id: string; label: string; message: string }[] = [];
  if (host) {
    const { u } = axesOf(unit.geometry.rotationDeg);
    const sideReach = Math.max(c.left ?? 0, c.right ?? 0) / 1000;
    const tc = dot(sub({ x: unit.geometry.x, y: unit.geometry.y }, host.run.origin), host.run.dir);
    const half = (W / 2) * Math.abs(dot(u, host.run.dir));
    const a = tc - half - sideReach;
    const b = tc + half + sideReach;
    for (const opening of openingsOnRun(host.run, scene)) {
      if (Math.min(b, opening.b) - Math.max(a, opening.a) <= TOL_M) continue;
      const behind = Math.min(tc + half, opening.b) - Math.max(tc - half, opening.a) > TOL_M;
      const what = opening.segment.kind === "window" ? "window" : opening.segment.kind === "door" ? "door" : "opening";
      openings.push({
        id: opening.segment.id,
        label: `${opening.segment.label} (${opening.segment.roomLabel})`,
        message:
          `${behind ? "Directly behind the unit" : "Within the unit's side clearance"}: the ${what} ${opening.segment.label} of ${opening.segment.roomLabel}. ` +
          "Check the installation manual's rules on openings near the unit — R290 units have a safety zone round them that must be clear of windows, doors and vents.",
      });
    }
  }

  if (!c.any) {
    return {
      state: "unknown",
      source: "none",
      items: [
        {
          side: "sides",
          state: "unknown",
          message:
            "Clearances not specified — enter them from the installation manual. None are held for this unit, and none are assumed.",
        },
      ],
      openings,
      ...(aboveNote ? { aboveNote } : {}),
    };
  }

  // Back: to the wall's outside face — which needs the wall's thickness.
  if (c.back !== undefined) {
    const need = c.back / 1000;
    if (!host) {
      items.push({
        side: "back",
        requiredMm: c.back,
        state: "pass",
        message: `Back: ${c.back} mm needed; no wall within ${HOST_WALL_REACH_M} m behind the unit on the plan.`,
      });
    } else if (host.facesWall) {
      items.push({
        side: "back",
        requiredMm: c.back,
        state: "fail",
        message: "The unit's FRONT faces the wall — its fan would blow at it. Rotate it so its back is to the wall.",
      });
    } else if (host.gapM + TOL_M < need) {
      items.push({
        side: "back",
        requiredMm: c.back,
        state: "fail",
        message:
          `Back: only ${mmText(host.gapM)} between the unit and the wall's inside face as drawn; the manufacturer asks for ${c.back} mm to its OUTSIDE face, which is nearer still. Move the unit out.`,
      });
    } else if (thicknessMm === undefined) {
      items.push({
        side: "back",
        requiredMm: c.back,
        state: "unknown",
        message:
          `Back: ${mmText(host.gapM)} from the unit to the wall's inside face (the only face the scan sees). The manufacturer asks for ${c.back} mm to its outside face — enter the wall's thickness to check it.`,
      });
    } else {
      const real = host.gapM - t;
      items.push({
        side: "back",
        requiredMm: c.back,
        state: real < -TOL_M ? "fail" : real + TOL_M >= need ? "pass" : "fail",
        message:
          real < -TOL_M
            ? `Back: drawn inside the wall — ${mmText(host.gapM)} from its inside face, and the wall is ${thicknessMm} mm thick.`
            : `Back: ${mmText(real)} to the wall's outside face (${mmText(host.gapM)} to the inside face less your ${thicknessMm} mm wall); ${c.back} mm needed.`,
      });
    }
  } else {
    items.push({ side: "back", state: "unknown", message: "Back clearance not specified — enter it from the installation manual." });
  }

  // Front: into the garden — clear of everything scanned.
  if (c.front !== undefined) {
    const zone = localRect(unit, -W / 2 - ZONE_GROW_M, W / 2 + ZONE_GROW_M, D / 2, D / 2 + c.front / 1000 + t);
    const frontEdge = localRect(unit, -W / 2, W / 2, D / 2, D / 2);
    const blocked = blockerIn(zone, [frontEdge[0]!, frontEdge[1]!], scene, new Set(), others.filter((o) => o.id !== unit.id), t);
    items.push(
      blocked && blocked.distanceM + TOL_M < c.front / 1000
        ? {
            side: "front",
            requiredMm: c.front,
            state: "fail",
            message: `Front: ${c.front} mm needed, but ${blocked.label} is ${mmText(blocked.distanceM)} in front of the unit.`,
          }
        : {
            side: "front",
            requiredMm: c.front,
            state: "pass",
            message: `Front: ${c.front} mm clear of everything scanned. Fences, boundaries and plants are not in the scan — check them on site.`,
          },
    );
  } else {
    items.push({ side: "front", state: "unknown", message: "Front clearance not specified — enter it from the installation manual." });
  }

  // Sides. Which end the manual calls "left" is its drawing's, not the plan's,
  // so an unequal pair is tried both ways round.
  const left = c.left;
  const right = c.right;
  if (left === undefined || right === undefined) {
    items.push({
      side: "sides",
      state: "unknown",
      message: `Side clearances not specified${left !== undefined || right !== undefined ? " for both sides" : ""} — enter them from the installation manual.`,
    });
  } else {
    const end = (sign: -1 | 1, needMm: number): { ok: boolean; text: string; pastEnd: boolean } => {
      const need = needMm / 1000;
      const s0 = sign < 0 ? -W / 2 - need - t : W / 2;
      const s1 = sign < 0 ? -W / 2 : W / 2 + need + t;
      const zone = localRect(unit, s0, s1, -D / 2 - ZONE_GROW_M, D / 2 + ZONE_GROW_M);
      const edgeRect = localRect(unit, sign * (W / 2), sign * (W / 2), -D / 2, D / 2);
      const blocked = blockerIn(zone, [edgeRect[0]!, edgeRect[3]!], scene, hostIds, others.filter((o) => o.id !== unit.id), t);
      let pastEnd = false;
      if (host) {
        const { u } = axesOf(unit.geometry.rotationDeg);
        const tc = dot(sub({ x: unit.geometry.x, y: unit.geometry.y }, host.run.origin), host.run.dir);
        const along = sign * dot(u, host.run.dir);
        const reach = tc + along * (W / 2 + need);
        pastEnd = reach < host.run.lo - TOL_M || reach > host.run.hi + TOL_M;
      }
      if (blocked && blocked.distanceM + TOL_M < need) {
        return { ok: false, text: `${blocked.label} is ${mmText(blocked.distanceM)} from that side`, pastEnd };
      }
      return { ok: true, text: "", pastEnd };
    };
    const tryWay = (a: number, b: number) => ({ lo: end(-1, a), hi: end(1, b) });
    const one = tryWay(left, right);
    const other = left === right ? one : tryWay(right, left);
    const okOne = one.lo.ok && one.hi.ok;
    const okOther = other.lo.ok && other.hi.ok;
    const pastEnd = (okOne ? [one.lo, one.hi] : [other.lo, other.hi]).some((x) => x.pastEnd);
    const pair = left === right ? `${left} mm each side` : `${Math.min(left, right)} mm one side and ${Math.max(left, right)} mm the other`;
    if (okOne && okOther) {
      items.push({
        side: "sides",
        state: pastEnd ? "warn" : "pass",
        message: pastEnd
          ? `Sides: ${pair} is clear of everything scanned, but reaches past the end of this wall — round a corner the scan did not see. Check on site.`
          : `Sides: ${pair} — clear.`,
      });
    } else if (okOne || okOther) {
      items.push({
        side: "sides",
        state: "warn",
        message:
          `Sides: ${pair} fits only one way round — the ${Math.max(left, right)} mm side must be at the end with more room. ` +
          "Check which side the installation manual means before fixing it.",
      });
    } else {
      const why = [one.lo, one.hi].filter((x) => !x.ok).map((x) => x.text)[0] ?? "a wall is too close";
      items.push({ side: "sides", state: "fail", message: `Sides: ${pair} needed, but ${why}.` });
    }
  }

  const states = items.map((i) => i.state);
  const state: CheckState = states.includes("fail")
    ? "fail"
    : states.includes("unknown")
      ? "unknown"
      : states.includes("warn") || openings.length > 0
        ? "warn"
        : "pass";
  return { state, source: c.source, items, openings, ...(aboveNote ? { aboveNote } : {}) };
}

// ----------------------------------------------------------- directivity --

function checkDirectivity(
  unit: HeatPump,
  walls: readonly PlanWall[],
  jurisdiction: Jurisdiction,
  calculationDate: string,
): OutdoorUnitCheck["directivity"] {
  const rules = resolveRuleset(NOISE_RULESETS, { on: calculationDate, jurisdiction }).data;
  const within = rules.reflectingSurfaceWithinM;
  const extent = rules.reflectingSurfaceMinExtentM;
  const design = unit.spec.design;
  const thicknessMm = design?.wallThicknessMm;
  const t = thicknessMm !== undefined ? thicknessMm / 1000 : undefined;
  const poly = placedCorners(unit.geometry, unit.spec.widthMm / 1000, unit.spec.depthMm / 1000);
  const c = { x: unit.geometry.x, y: unit.geometry.y };
  const notes: string[] = [];

  const definite: { wall: PlanWall; distanceM: number }[] = [];
  const possible: { wall: PlanWall; distanceM: number }[] = [];
  for (const wall of walls) {
    // Only a wall whose OUTSIDE faces the unit can reflect its sound.
    if (dot(sub(c, wall.start), wall.inward) >= 0) continue;
    const scanned = polygonSegmentDistance(poly, wall.start, wall.end);
    const real = t !== undefined ? scanned - t : scanned;
    if (real <= within + TOL_M) definite.push({ wall, distanceM: scanned });
    else if (t === undefined && scanned <= within + THICK_WALL_M) possible.push({ wall, distanceM: scanned });
  }

  // One reflecting surface per plane: pieces of one façade count once.
  const planes: { walls: { wall: PlanWall; distanceM: number }[] }[] = [];
  for (const entry of definite) {
    const d = sub(entry.wall.end, entry.wall.start);
    const len = Math.hypot(d.x, d.y);
    const dir = { x: d.x / len, y: d.y / len };
    const plane = planes.find((p) => {
      const ref = p.walls[0]!.wall;
      const rd = sub(ref.end, ref.start);
      const rl = Math.hypot(rd.x, rd.y);
      if (Math.abs(cross(dir, { x: rd.x / rl, y: rd.y / rl })) > PARALLEL_SIN) return false;
      return Math.abs(dot(sub(entry.wall.start, ref.start), ref.inward)) <= SAME_PLANE_M;
    });
    if (plane) plane.walls.push(entry);
    else planes.push({ walls: [entry] });
  }

  const surfaces: ReflectingSurface[] = [];
  const mounting = design?.mounting;
  if (mounting !== "wall-bracket") {
    surfaces.push({
      kind: "ground",
      wallIds: [],
      distanceM: 0,
      note:
        mounting === "ground"
          ? "Standing on the ground."
          : "Mounting not stated, so taken as standing on the ground — the louder case. Set it if the unit is on wall brackets.",
    });
  } else {
    notes.push(
      `On wall brackets, so the ground is not counted — correct only if the unit is more than ${within} m above it. If it is lower, the ground counts too: override Q.`,
    );
  }
  for (const plane of planes) {
    const ids = plane.walls.map((w) => w.wall.id);
    const distanceM = Math.min(...plane.walls.map((w) => w.distanceM));
    // Step 4: a surface counts only if it runs ≥1 m past the unit's edge in two
    // or more directions. Up is almost always one for a house wall; sideways is
    // checked here, and a short one is still counted (louder) but named.
    const ref = plane.walls[0]!.wall;
    const d = sub(ref.end, ref.start);
    const len = Math.hypot(d.x, d.y);
    const dir = { x: d.x / len, y: d.y / len };
    const ts = plane.walls.flatMap((w) => [dot(sub(w.wall.start, ref.start), dir), dot(sub(w.wall.end, ref.start), dir)]);
    const us = poly.map((p) => dot(sub(p, ref.start), dir));
    const beyondLo = Math.min(...us) - Math.min(...ts);
    const beyondHi = Math.max(...ts) - Math.max(...us);
    const short = beyondLo < extent - TOL_M && beyondHi < extent - TOL_M;
    surfaces.push({
      kind: "wall",
      wallIds: ids,
      distanceM,
      ...(short
        ? {
            note: `Runs less than ${extent} m past the unit on both sides (MCS 020 a) Step 4 counts a surface only if it extends ≥${extent} m beyond the unit in two directions) — counted anyway, the louder case; override Q if it should not be.`,
          }
        : {}),
    });
  }
  if (possible.length > 0) {
    notes.push(
      `${possible.length === 1 ? "A wall is" : `${possible.length} walls are`} between ${within} m and ${within + THICK_WALL_M} m from the unit on the scan — ` +
        `within ${within} m of its outside face if the wall is thick enough. Enter the wall thickness, or override Q.`,
    );
  }

  const count = surfaces.length;
  const table = rules.directivityByReflectingSurfaces;
  let derivedQ: number;
  let state: CheckState;
  let message: string;
  if (count > 3) {
    derivedQ = 16;
    state = "fail";
    message = `${count} reflecting surfaces within ${within} m — more than three does not meet MCS 020 a) (Step 4), whatever the level.`;
  } else {
    derivedQ = count === 0 ? table[1] : table[count as 1 | 2 | 3];
    state = possible.length > 0 || surfaces.some((s) => s.note && s.kind === "wall") || mounting === undefined ? "warn" : "pass";
    const describe = surfaces
      .map((s) => (s.kind === "ground" ? "the ground" : `a wall ${s.distanceM < 0.01 ? "behind it" : `${mmText(s.distanceM)} away (scanned face)`}`))
      .join(" and ");
    message =
      count === 0
        ? `No reflecting surface found within ${within} m; the lowest factor MCS 020 a) uses, Q${table[1]}, is taken.`
        : `Q${derivedQ}: ${count} reflecting surface${count === 1 ? "" : "s"} within ${within} m — ${describe}.`;
  }
  const override = design?.directivityOverride;
  if (override !== undefined) {
    notes.unshift(`Your Q${override} is used in place of the plan's Q${derivedQ}.`);
    if (state !== "fail") state = "pass";
  }
  return {
    state,
    q: override ?? derivedQ,
    derivedQ,
    overridden: override !== undefined,
    surfaces,
    message,
    notes,
  };
}

// ----------------------------------------------------------------- noise --

/**
 * MORE THAN ONE OUTDOOR UNIT. MCS 020 a) assesses the level AT an assessment
 * position, not per unit: "Where two heat pumps are installed and have the
 * same assessment position(s), the sound pressure level at the assessment
 * position for each heat pump shall be calculated and then the total sound
 * pressure level shall be calculated using the logarithmic summation"
 * (Issue 1.1, Step 7, p.14; Table 2 Steps 7a/7b, p.10), "expanded for more
 * than two heat pumps"; Step 8 then rounds that TOTAL to 0.1 and compares it
 * with 37.0 dB(A). Step 2 (p.11): "consider all assessment positions for each
 * heat pump … the distances or line of sight may be different."
 *
 * Positions are stored on each unit (`spec.design.noisePositions`), so:
 *
 *   - A position PLACED on the plan is a point every unit on that floor can be
 *     measured to, so every one of them is added there — each with its own
 *     sound power, its own Q (from its own placement) and its own distance
 *     from its centre. Counting a unit that is in fact out of earshot only
 *     makes the total louder, never quieter.
 *   - Each unit's barrier and line of sight to the window are its own (Step 6).
 *     They are read from that unit's own entry for the same window — a point of
 *     its own within `SAME_WINDOW_M` — and where it has none, no barrier and a
 *     full view are taken (the louder case), and the working says so. A typed
 *     distance on that entry is that unit's measured distance and is used.
 *   - A TYPED distance is one unit's distance to the window; the others cannot
 *     be measured to it. Such a position is assessed for its unit alone and
 *     shown amber on a site with other units, asking for the point on the plan.
 *   - Floors are scanned separately with no common frame (`@sts/geometry`
 *     dwelling.ts), so a unit on another floor cannot be measured to a point:
 *     it is named as left out, amber, never silently dropped.
 *
 * MCS 020 a) has no background-noise or rating step (that is the legacy
 * MCS 020 v1.3 method, not implemented here): Step 7b's total goes straight to
 * the Step 8 rounding and the limit.
 */
interface NoiseSourceUnit {
  readonly unit: HeatPump;
  /** "HP2" */
  readonly tag: string;
  /** "HP2 (Vaillant aroTHERM plus 7 kW)" */
  readonly label: string;
  /** The Q its own placement gives (or the designer's override). */
  readonly q: number;
  readonly lw: number | undefined;
}

/**
 * Two units' entries whose points are within this are the same window. Half
 * the 1 m the assessment position stands off its window: two different
 * windows' positions are further apart than that, and two clicks on one
 * window are nearer.
 */
const SAME_WINDOW_M = 0.5;

/** "HP" for one unit; "HP1", "HP2" … in storey then drawing order — the plan exports' and installer pack's tags. */
function heatPumpTags(units: readonly HeatPump[]): Map<string, string> {
  const ordered = [...units].sort((a, b) => a.storey - b.storey);
  return new Map(ordered.map((u, i) => [u.id, ordered.length === 1 ? "HP" : `HP${i + 1}`]));
}

const BARRIER_TEXT: Readonly<Record<NoiseAssessmentPoint["barrierType"], string>> = {
  none: "none",
  "type-1": "Type 1 (solid, ≥18 mm)",
  "type-2": "Type 2 (solid, <18 mm)",
};

const listText = (items: readonly string[]): string =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

function checkNoise(
  unit: HeatPump,
  q: number,
  directivityState: CheckState,
  jurisdiction: Jurisdiction,
  calculationDate: string,
  site: readonly NoiseSourceUnit[],
): OutdoorUnitCheck["noise"] {
  const ruleset = resolveRuleset(NOISE_RULESETS, { on: calculationDate, jurisdiction });
  const rules = ruleset.data;
  const rulesetLabel = `${ruleset.source.standard} issue ${ruleset.source.issue} (${jurisdiction}), limit ${rules.limitDb.toFixed(1)} dB(A) ${rules.metric}`;
  const design = unit.spec.design;
  const notes: string[] = [];
  const sheet = datasheetFor(unit.spec.catalogueId)?.soundPower ?? null;
  const soundPowerPrefill =
    sheet && !sheet.uncertain
      ? {
          dbA: sheet.dbA,
          condition: sheet.condition,
          citation: citeDatasheet(datasheetSource(sheet.source)),
          label: DATASHEET_LABEL,
        }
      : undefined;
  if (sheet && sheet.uncertain) {
    notes.push(
      `The datasheet figure found for this unit (${sheet.dbA} dB(A), ${sheet.condition}) was marked uncertain by the researcher, so it is not offered — confirm it with the manufacturer or the MCS Product Directory.`,
    );
  }
  if (ruleset.provenance === "provisional") {
    notes.push(
      `${ruleset.source.standard} issue ${ruleset.source.issue} applies in ${jurisdiction}, and its method (which accounts for background noise) is not implemented — the figures below use the MCS 020 a) arithmetic and are indicative only.`,
    );
  }

  const base = {
    limitDb: rules.limitDb,
    metric: rules.metric,
    rulesetLabel,
    ...(soundPowerPrefill ? { soundPowerPrefill } : {}),
  };
  const lw = design?.soundPowerDbA;
  const positions = design?.noisePositions ?? [];

  // Windows placed for the other units on this floor that this one has no
  // entry for: its sound is added there too (Step 7b), so say where.
  const ownPoints = positions.flatMap((p) => (p.point ? [p.point] : []));
  const elsewhere: string[] = [];
  for (const other of site) {
    if (other.unit.id === unit.id || other.unit.storey !== unit.storey) continue;
    for (const p of other.unit.spec.design?.noisePositions ?? []) {
      const at = p.point;
      if (!at) continue;
      if (ownPoints.some((o) => Math.hypot(o.x - at.x, o.y - at.y) <= SAME_WINDOW_M)) continue;
      elsewhere.push(`${p.description.trim() || "a window"} (placed for ${other.label})`);
    }
  }
  if (elsewhere.length > 0) {
    notes.push(
      lw === undefined
        ? `Its sound power is needed at ${listText(elsewhere)}, where every unit's sound is added together (MCS 020 a) Step 7b).`
        : `Its sound is also added at ${listText(elsewhere)} (MCS 020 a) Step 7b) — see that unit's check. With no entry of its own there, no barrier is taken for it; add the window to this unit too to set one.`,
    );
  }

  if (lw === undefined) {
    return {
      ...base,
      state: "unknown",
      message: "Sound power not entered — enter the unit's EN 12102 sound power level (not low-noise mode) to assess it.",
      positions: [],
      notes,
    };
  }
  const soundPower = { dbA: lw, source: design?.soundPowerSource ?? "your figure" };
  if (positions.length === 0) {
    return {
      ...base,
      state: "unknown",
      soundPower,
      message:
        `Place the neighbour's window on the plan (a point ${rules.assessmentPositionOffsetM} m outside it), or type its distance, to assess MCS 020 a).`,
      positions: [],
      notes,
    };
  }

  const results: NoisePositionResult[] = positions.map((position) =>
    assessPosition(unit, position, lw, q, jurisdiction, calculationDate, rules, site),
  );
  const leftOutFor = (reason: NoiseUnitLeftOut["reason"]): NoisePositionResult[] =>
    results.filter((r) => r.leftOut?.some((l) => l.reason === reason));
  const silent = [
    ...new Set(results.flatMap((r) => (r.leftOut ?? []).filter((l) => l.reason === "no-sound-power").map((l) => l.label))),
  ];
  if (silent.length > 0) {
    notes.push(
      `${listText(silent)} ${silent.length === 1 ? "has" : "have"} no sound power entered, so ${silent.length === 1 ? "its" : "their"} sound cannot yet be added at the windows placed here — ` +
        "MCS 020 a) Step 7b adds every unit that reaches a window. Enter it on that unit.",
    );
  }
  const typedAlone = leftOutFor("typed-distance");
  if (typedAlone.length > 0) {
    notes.push(
      `${site.length} outdoor units are on the plan, and ${typedAlone.length === 1 ? "one window is" : `${typedAlone.length} windows are`} given only as a typed distance — ` +
        "a typed distance is this unit's alone, so the others' sound is not added there. Place each such window on the plan and every unit's sound is added (MCS 020 a) Step 7b).",
    );
  }
  const otherFloors = site.filter((s) => s.unit.storey !== unit.storey);
  if (otherFloors.length > 0 && leftOutFor("other-floor").length > 0) {
    notes.push(
      `${listText(otherFloors.map((s) => s.label))} ${otherFloors.length === 1 ? "is" : "are"} on another floor: floors are scanned separately, so the plan cannot measure ${otherFloors.length === 1 ? "it" : "them"} to these windows and ${otherFloors.length === 1 ? "its" : "their"} sound is not added. ` +
        "Add it by hand (MCS 020 a) Step 7b) wherever the same window is in earshot of both.",
    );
  }
  const states = results.map((r) => r.state);
  let state: CheckState = states.includes("fail")
    ? "fail"
    : states.includes("unknown")
      ? "unknown"
      : states.includes("warn")
        ? "warn"
        : "pass";
  if (state === "pass" && (directivityState === "warn" || ruleset.provenance === "provisional")) state = "warn";
  if (directivityState === "fail") state = "fail";
  const loudest = results
    .filter((r) => r.levelDb !== undefined)
    .sort((a, b) => (b.levelDb as number) - (a.levelDb as number))[0];
  const together = loudest?.contributions
    ? ` (${listText(loudest.contributions.map((c) => c.tag))} together)`
    : "";
  const message = loudest
    ? `${loudest.levelDb!.toFixed(1)} dB(A) at ${loudest.description}${together} against the ${rules.limitDb.toFixed(1)} dB(A) limit — ${
        state === "fail" ? "does not meet" : state === "pass" ? "meets" : "meets on these inputs, but see the notes"
      } MCS 020 a).`
    : (results.find((r) => r.leftOut?.some((l) => l.reason === "no-sound-power"))?.message ??
      "No assessment position could be measured.");
  return { ...base, state, soundPower, message, positions: results, notes };
}

/** The distance MCS 020 a) Step 5 uses: to the nearest 0.1 m. */
const toTenth = (m: number): number => Math.round(m * 10) / 10;

function assessPosition(
  unit: HeatPump,
  position: NoiseAssessmentPoint,
  lw: number,
  q: number,
  jurisdiction: Jurisdiction,
  calculationDate: string,
  rules: NoiseRules,
  site: readonly NoiseSourceUnit[],
): NoisePositionResult {
  const limitDb = rules.limitDb;
  const typed = position.distanceM;
  const planDistance = position.point
    ? Math.hypot(position.point.x - unit.geometry.x, position.point.y - unit.geometry.y)
    : undefined;
  const distanceM = typed ?? planDistance;
  const description = position.description.trim() || "the assessment position";
  if (distanceM === undefined || !(distanceM > 0)) {
    return {
      id: position.id,
      description,
      state: "unknown",
      limitDb,
      message: `${description}: no distance — place it on the plan or type the measured distance.`,
      working: [],
    };
  }
  if (!(toTenth(distanceM) > 0)) {
    return {
      id: position.id,
      description,
      state: "unknown",
      limitDb,
      message: `${description}: less than 0.05 m from the unit — MCS 020 a) Step 5 measures to 0.1 m. Move the point to 1 m outside the neighbour's window.`,
      working: [],
    };
  }

  // The other outdoor units, and which of them reach this position.
  const others = site.filter((s) => s.unit.id !== unit.id);
  const leftOut: NoiseUnitLeftOut[] = [];
  const reaching: { source: NoiseSourceUnit; lw: number; distanceM: number; basis: "plan" | "typed"; entry?: NoiseAssessmentPoint }[] = [];
  const at = position.point;
  for (const other of others) {
    const leave = (reason: NoiseUnitLeftOut["reason"]) => leftOut.push({ objectId: other.unit.id, tag: other.tag, label: other.label, reason });
    if (!at) {
      leave("typed-distance");
      continue;
    }
    if (other.unit.storey !== unit.storey) {
      leave("other-floor");
      continue;
    }
    // Its own entry for this window, if it has one: the nearest of its points within SAME_WINDOW_M.
    let entry: NoiseAssessmentPoint | undefined;
    let nearest = Infinity;
    for (const p of other.unit.spec.design?.noisePositions ?? []) {
      if (!p.point) continue;
      const d = Math.hypot(p.point.x - at.x, p.point.y - at.y);
      if (d <= SAME_WINDOW_M && d < nearest) {
        entry = p;
        nearest = d;
      }
    }
    const typedThere = entry?.distanceM ?? null;
    const d = typedThere ?? Math.hypot(at.x - other.unit.geometry.x, at.y - other.unit.geometry.y);
    if (!(toTenth(d) > 0)) {
      leave("at-the-window");
      continue;
    }
    if (other.lw === undefined) {
      leave("no-sound-power");
      continue;
    }
    reaching.push({
      source: other,
      lw: other.lw,
      distanceM: d,
      basis: typedThere !== null ? "typed" : "plan",
      ...(entry ? { entry } : {}),
    });
  }

  const result = assessNoise({
    jurisdiction,
    calculationDate,
    complianceMode: false,
    positions: [
      {
        id: position.id,
        description,
        sources: [
          {
            id: unit.id,
            soundPowerLevelDb: lw,
            directivityFactor: q,
            distanceM,
            barrierType: position.barrierType,
            lineOfSight: position.lineOfSight,
          },
          ...reaching.map((o) => ({
            id: o.source.unit.id,
            soundPowerLevelDb: o.lw,
            directivityFactor: o.source.q,
            distanceM: o.distanceM,
            barrierType: o.entry?.barrierType ?? ("none" as const),
            lineOfSight: o.entry?.lineOfSight ?? ("full-view" as const),
          })),
        ],
      },
    ],
  });
  const pos = result.positions[0]!;
  const ab = pos.contributions[0]?.barrierAttenuationDb ?? 0;
  const r = toTenth(distanceM);
  const typedHere = typed !== null && typed !== undefined;
  const distanceLine = typedHere
    ? `r = ${r.toFixed(1)} m, as typed (Step 5: unit centre to the assessment position, to 0.1 m).`
    : `r = ${r.toFixed(1)} m across the plan from the unit's centre — horizontal only, so shorter than the straight line to an upstairs window (the louder reading). Type the measured distance to replace it.`;
  const barrierLine = `Barrier: ${BARRIER_TEXT[position.barrierType]}, ${position.lineOfSight.replace("-", " ")} → A_B = ${ab} dB (Step 6).`;
  // Only this unit's own Q can fail this position outright: another unit's
  // Q outside {2, 4, 8} fails THAT unit's own check, and still adds its sound here.
  const ownQPermitted = rules.permittedDirectivityFactors.includes(q);
  const assumedBarrier = position.barrierType === "none" && position.lineOfSight === "full-view";

  // ---- one unit reaches it: exactly as before --------------------------------
  if (reaching.length === 0) {
    const withinLimit = pos.withinLimit;
    const missing = leftOut.filter((l) => l.reason === "no-sound-power");
    const state: CheckState = !withinLimit || !ownQPermitted ? "fail" : missing.length > 0 ? "unknown" : leftOut.length > 0 ? "warn" : "pass";
    const working = [
      `Lp = Lw + 10·log10(Q / (4·π·r²)) − A_B  (MCS 020 a) Step 7)`,
      `   = ${lw} + 10·log10(${q} / (4·π·${r.toFixed(1)}²)) − ${ab}`,
      `   = ${pos.totalSoundPressureLevelDb.toFixed(2)} → ${pos.reportedSoundPressureLevelDb.toFixed(1)} dB(A) (Step 8, to 0.1)`,
      distanceLine,
      barrierLine,
    ];
    const level = `${pos.reportedSoundPressureLevelDb.toFixed(1)} dB(A)`;
    const rest = `at ${r.toFixed(1)} m (limit ${limitDb.toFixed(1)})${assumedBarrier ? ", no barrier" : ""}`;
    const message =
      leftOut.length === 0
        ? `${description}: ${level} ${rest} — ${state === "pass" ? "meets" : "over the limit"}.`
        : `${description}: ${level} from this unit alone ${rest} — ${leftOutText(leftOut, state === "fail")}`;
    return {
      id: position.id,
      description,
      state,
      distanceM: r,
      distanceBasis: typedHere ? "typed" : "plan",
      barrierAttenuationDb: ab,
      // A level the other units could only raise is shown when it already fails.
      ...(missing.length === 0 || state === "fail" ? { levelDb: pos.reportedSoundPressureLevelDb } : {}),
      limitDb,
      message,
      working,
      ...(leftOut.length > 0 ? { leftOut } : {}),
    };
  }

  // ---- two or more units reach it: MCS 020 a) Table 2, Steps 7a, 7b, 8 -------
  const self = site.find((s) => s.unit.id === unit.id);
  const contributions: NoiseContribution[] = [
    {
      objectId: unit.id,
      tag: self?.tag ?? "HP",
      label: self?.label ?? unit.spec.name,
      soundPowerDbA: lw,
      q,
      distanceM: r,
      distanceBasis: typedHere ? "typed" : "plan",
      barrierType: position.barrierType,
      lineOfSight: position.lineOfSight,
      barrierAttenuationDb: ab,
      barrierAssumed: false,
      levelDb: pos.contributions[0]!.soundPressureLevelDb,
    },
    ...reaching.map((o, i) => ({
      objectId: o.source.unit.id,
      tag: o.source.tag,
      label: o.source.label,
      soundPowerDbA: o.lw,
      q: o.source.q,
      distanceM: toTenth(o.distanceM),
      distanceBasis: o.basis,
      barrierType: o.entry?.barrierType ?? ("none" as const),
      lineOfSight: o.entry?.lineOfSight ?? ("full-view" as const),
      barrierAttenuationDb: pos.contributions[i + 1]!.barrierAttenuationDb,
      barrierAssumed: o.entry === undefined,
      levelDb: pos.contributions[i + 1]!.soundPressureLevelDb,
    })),
  ];
  // This unit on its own, rounded as Step 8 — so the designer sees what the others add.
  const alone = assessNoise({
    jurisdiction,
    calculationDate,
    complianceMode: false,
    positions: [
      {
        id: position.id,
        description,
        sources: [
          {
            id: unit.id,
            soundPowerLevelDb: lw,
            directivityFactor: q,
            distanceM,
            barrierType: position.barrierType,
            lineOfSight: position.lineOfSight,
          },
        ],
      },
    ],
  }).positions[0]!.reportedSoundPressureLevelDb;

  const tag = (c: NoiseContribution): string => c.tag;
  const unitLine = (c: NoiseContribution, self: boolean): string => {
    const how =
      c.distanceBasis === "typed"
        ? `r ${c.distanceM.toFixed(1)} m as typed`
        : `r ${c.distanceM.toFixed(1)} m across the plan from its centre`;
    const barrier = c.barrierAssumed
      ? `no barrier set for ${tag(c)} at this window — none, full view taken (the louder case); add this window to ${tag(c)} to set it`
      : `${BARRIER_TEXT[c.barrierType]}, ${c.lineOfSight.replace("-", " ")} → A_B ${c.barrierAttenuationDb} dB`;
    return (
      `  ${c.label}${self ? " — this unit" : ""}: ${c.soundPowerDbA} + 10·log10(${c.q} / (4·π·${c.distanceM.toFixed(1)}²)) − ${c.barrierAttenuationDb} = ${c.levelDb.toFixed(2)} dB(A)` +
      `  [Q${c.q}; ${how}; ${barrier}]`
    );
  };
  const working = [
    `MCS 020 a) Table 2 — ${contributions.length} outdoor units reach this position; each is worked on its own, then added.`,
    `Step 7a, each unit: Lp = Lw + 10·log10(Q / (4·π·r²)) − A_B`,
    ...contributions.map((c, i) => unitLine(c, i === 0)),
    `Step 7b: Lp,total = 10·log10(${contributions.map((c) => `10^(${c.levelDb.toFixed(2)}/10)`).join(" + ")})`,
    `   = ${pos.totalSoundPressureLevelDb.toFixed(2)} → ${pos.reportedSoundPressureLevelDb.toFixed(1)} dB(A) (Step 8, to 0.1; summed from the unrounded levels)`,
    ...(contributions.some((c) => c.distanceBasis === "plan")
      ? ["Distances across the plan are horizontal only, so shorter than the straight line to an upstairs window (the louder reading). Type a unit's measured distance on its own entry for this window to replace it."]
      : []),
  ];
  const withinLimit = pos.withinLimit;
  const missing = leftOut.filter((l) => l.reason === "no-sound-power");
  const state: CheckState = !withinLimit || !ownQPermitted ? "fail" : missing.length > 0 ? "unknown" : leftOut.length > 0 ? "warn" : "pass";
  const total = pos.reportedSoundPressureLevelDb.toFixed(1);
  const head =
    `${description}: ${total} dB(A) from ${listText(contributions.map(tag))} together ` +
    `(this unit alone ${alone.toFixed(1)} dB(A) at ${r.toFixed(1)} m; limit ${limitDb.toFixed(1)})`;
  const message =
    leftOut.length === 0
      ? `${head} — ${state === "pass" ? "meets" : "over the limit"}.`
      : `${head} — ${leftOutText(leftOut, state === "fail")}`;
  return {
    id: position.id,
    description,
    state,
    distanceM: r,
    distanceBasis: typedHere ? "typed" : "plan",
    barrierAttenuationDb: ab,
    ...(missing.length === 0 || state === "fail" ? { levelDb: pos.reportedSoundPressureLevelDb } : {}),
    limitDb,
    message,
    working,
    contributions,
    unitLevelDb: alone,
    ...(leftOut.length > 0 ? { leftOut } : {}),
  };
}

/** What a position's level leaves out, in words, ending the message. */
function leftOutText(leftOut: readonly NoiseUnitLeftOut[], failing: boolean): string {
  const tags = (reason: NoiseUnitLeftOut["reason"]) =>
    listText(leftOut.filter((l) => l.reason === reason).map((l) => l.tag));
  const parts: string[] = [];
  const has = (reason: NoiseUnitLeftOut["reason"]) => leftOut.some((l) => l.reason === reason);
  if (has("no-sound-power")) parts.push(`${tags("no-sound-power")} has no sound power entered, so cannot be added yet`);
  if (has("typed-distance"))
    parts.push(`a typed distance is this unit's alone, so ${tags("typed-distance")} cannot be measured to it: place this window on the plan to add every unit's sound`);
  if (has("other-floor")) parts.push(`${tags("other-floor")} is on another floor and cannot be measured to it on the plan — add it by hand`);
  if (has("at-the-window")) parts.push(`${tags("at-the-window")} is drawn on the point itself — move the point to 1 m outside the window`);
  const why = `${parts.join("; ")} (MCS 020 a) Step 7b).`;
  return failing ? `over the limit already, and the others can only add to it: ${why}` : `not yet the whole level: ${why}`;
}

// ------------------------------------------------------------------ entry --

export interface OutdoorUnitCheckInput {
  readonly rooms: readonly PlanRoom[];
  readonly objects: readonly PlanObjectOnPlan[];
  readonly jurisdiction: Jurisdiction;
  readonly calculationDate: string;
}

/** Every outdoor unit on the plan, each checked against its own storey's walls. */
export function checkOutdoorUnits(input: OutdoorUnitCheckInput): readonly OutdoorUnitCheck[] {
  const units = input.objects.filter((o): o is HeatPump => o.kind === "heat-pump");
  const tags = heatPumpTags(units);
  // Every unit's placement first — its Q is needed wherever its sound is added.
  const placed = units.map((unit) => {
    const storeyRooms = input.rooms.filter((r) => r.story === unit.storey);
    const scene = buildFitScene(storeyRooms);
    const host = hostWallOf(unit, scene.walls);
    const others = units.filter((u) => u.storey === unit.storey && u.id !== unit.id);
    const catalogue = unit.spec.catalogueId ? outdoorUnitById(unit.spec.catalogueId) : undefined;

    const wall: OutdoorUnitCheck["wall"] = host
      ? {
          state: host.facesWall ? "fail" : "pass",
          message: host.facesWall
            ? "Its front faces the wall — rotate it so its back is to the wall."
            : `Against ${host.run.wallIds.size > 1 ? "a wall of " : ""}${scene.wallSegments.get(host.wall.id)?.roomLabel ?? "the house"} (${scene.wallSegments.get(host.wall.id)?.label ?? host.wall.id}), ${mmText(host.gapM)} from its inside face.`,
          hostWallIds: [...host.run.wallIds],
          backGapM: host.gapM,
        }
      : {
          state: "warn",
          message: `Not against a wall — no scanned wall within ${HOST_WALL_REACH_M} m behind it, so it is taken as free-standing.`,
          hostWallIds: [],
        };

    const clearances = checkClearances(unit, host, scene, others);
    const directivity = checkDirectivity(unit, scene.walls, input.jurisdiction, input.calculationDate);
    return { unit, catalogue, wall, clearances, directivity };
  });
  const site: NoiseSourceUnit[] = placed.map(({ unit, directivity }) => {
    const tag = tags.get(unit.id) ?? "HP";
    return { unit, tag, label: `${tag} (${unit.spec.name})`, q: directivity.q, lw: unit.spec.design?.soundPowerDbA };
  });
  return placed.map(({ unit, catalogue, wall, clearances, directivity }) => {
    const noise = checkNoise(unit, directivity.q, directivity.state, input.jurisdiction, input.calculationDate, site);
    return {
      objectId: unit.id,
      name: unit.spec.name,
      storey: unit.storey,
      ...(catalogue
        ? { nominal: `${catalogue.sizeLabel} — the size on the box, not a sizing figure` }
        : {}),
      wall,
      clearances,
      directivity,
      noise,
    };
  });
}

